/**
 * Connexion OIDC au realm Keycloak `ultimate` — Authorization Code + PKCE (S256).
 *
 * oidc-client-ts fait le PKCE S256 de lui-même sur le flux code. Le client
 * `ultimate-web` est public : pas de secret, le code_verifier en tient lieu.
 *
 * Durée de vie, réglée sur le realm : jeton d'accès 15 min, session SSO 30 min
 * d'inactivité et 10 h au plus. Un coach garde son planning ouvert la journée ; la
 * session doit donc survivre à l'expiration du jeton, au rechargement et aux onglets.
 *
 * Rafraîchissement, deux chemins complémentaires, tous deux par refresh token — une
 * requête de fond vers Keycloak, jamais de redirection ni d'iframe (l'iframe dépend
 * des cookies tiers, bloqués entre localhost et auth.elmv.be) :
 *   - page ouverte : `automaticSilentRenew` renouvelle 60 s avant l'échéance, soit à
 *     14 min pour un jeton de 15. Chaque renouvellement repousse aussi l'inactivité
 *     SSO : un planning resté ouvert tient jusqu'aux 10 h de la session ;
 *   - jeton trouvé déjà expiré — portable réveillé, onglet rouvert le lendemain
 *     matin : oidc-client-ts n'arme alors aucun minuteur, c'est `utilisateurValide`
 *     qui renouvelle avant tout appel.
 *
 * Stockage : localStorage. La session survit à la fermeture de l'onglet et se partage
 * entre onglets. Chaque onglet renouvelle de son côté avec le même refresh token, ce
 * que Keycloak accepte tant que le realm ne révoque pas les refresh tokens à l'usage
 * (revokeRefreshToken, désactivé) — l'activer imposerait de coordonner les onglets.
 * Contrepartie assumée : un refresh token lisible par tout script de la page, comme
 * dans toute SPA sans backend ; la politique de contenu reste la vraie défense.
 */
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

const authority = import.meta.env.VITE_OIDC_AUTHORITY ?? "https://auth.elmv.be/realms/ultimate";
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID ?? "ultimate-web";

export const userManager = new UserManager({
  authority,
  client_id: clientId,
  // Les deux URI sont enregistrées telles quelles sur le client Keycloak
  // (migration/setup-ultimate-realm.sh) : ne pas y ajouter de barre finale.
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid profile email",
  automaticSilentRenew: true,
  // Valeur par défaut, écrite pour être lue : renouvellement à 14 min sur 15.
  accessTokenExpiringNotificationTimeInSeconds: 60,
  // La surveillance de session passe elle aussi par une iframe tierce.
  monitorSession: false,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

/** Clé sous laquelle oidc-client-ts range l'utilisateur dans le localStorage. */
export const CLE_UTILISATEUR = `oidc.user:${authority}:${clientId}`;

/** Chemin interne sûr pour y revenir après connexion — jamais une URL externe. */
function cheminSur(chemin: unknown): string {
  return typeof chemin === "string" && chemin.startsWith("/") && !chemin.startsWith("//")
    ? chemin
    : "/planning";
}

function cheminCourant(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

let renouvellement: Promise<User | null> | null = null;

/**
 * L'utilisateur connecté avec un jeton encore valide, ou `null`.
 *
 * Un jeton expiré — onglet endormi, portable fermé — est renouvelé ici avec le refresh
 * token avant d'abandonner. Les appels simultanés partagent le même renouvellement.
 */
export async function utilisateurValide(): Promise<User | null> {
  const user = await userManager.getUser();
  if (user && !user.expired) return user;
  if (!user?.refresh_token) return null;

  renouvellement ??= userManager
    .signinSilent()
    .catch(async () => {
      // Session Keycloak close ou refresh token périmé : repartir de zéro.
      await userManager.removeUser();
      return null;
    })
    .finally(() => {
      renouvellement = null;
    });
  return renouvellement;
}

let redirection: Promise<never> | null = null;

/**
 * Part vers la page de connexion Keycloak, en mémorisant où revenir.
 *
 * Ne se résout jamais : la page s'en va. Un appelant qui l'attend reste donc en
 * suspens au lieu d'afficher une erreur. Plusieurs appels simultanés — trois requêtes
 * en 401 d'un coup — ne déclenchent qu'une redirection.
 */
export function connexion(retour: string = cheminCourant()): Promise<never> {
  redirection ??= userManager
    .signinRedirect({ state: { retour } })
    .then(() => new Promise<never>(() => {}))
    .catch((err: unknown) => {
      redirection = null;
      throw err;
    });
  return redirection;
}

/**
 * Changer son mot de passe : Keycloak le fait lui-même (action `UPDATE_PASSWORD` demandée
 * par `kc_action`), puis revient sur `retour`. L'application ne voit jamais le mot de passe.
 */
export function changerMotDePasse(retour: string = cheminCourant()): Promise<void> {
  return userManager.signinRedirect({ state: { retour }, extraQueryParams: { kc_action: "UPDATE_PASSWORD" } });
}

export function deconnexion(): Promise<void> {
  return userManager.signoutRedirect();
}

let retourConnexion: Promise<string> | null = null;

/**
 * Traite le retour de Keycloak sur /auth/callback : échange le code contre les jetons
 * et renvoie le chemin d'où l'on était parti.
 *
 * Mémorisé : l'échange ne vaut qu'une fois — le StrictMode de React monte les effets
 * deux fois, et le second échange du même code échouerait.
 */
export function traiterRetourConnexion(): Promise<string> {
  retourConnexion ??= userManager
    .signinRedirectCallback()
    .then((user) => cheminSur((user.state as { retour?: unknown } | undefined)?.retour));
  return retourConnexion;
}

/** Nom à afficher : le nom complet des claims standard, à défaut le login. */
export function nomAffiche(user: User): string {
  const p = user.profile;
  return p.name ?? ([p.given_name, p.family_name].filter(Boolean).join(" ") || p.preferred_username || "");
}
